import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * Sentry configuration for the SPA: errors **and** performance.
 *
 * The performance half is why this exists and is not an extra: the backend
 * already reported errors, but measured only its own side. What a person
 * perceives as "Factit is slow" includes the bundle download, the render and
 * the wait on the fetch — and the server sees none of that. With
 * `browserTracing` the trace starts in the browser, travels in the
 * `sentry-trace`/`baggage` headers to the dyno and joins the backend's
 * transaction, so a slow issuance can be split into "the SII was slow" versus
 * "the screen was slow".
 *
 * Everything is decided by environment variables, and **with no DSN nothing is
 * initialised**: in dev and in the tests the SPA runs without the SDK.
 */

/** The sampling default, matching the backend's so both halves of a trace agree. */
export const DEFAULT_TRACES_SAMPLE_RATE = 0.1;

/** What `import.meta.env` contributes here. Explicit so this is testable without Vite. */
export interface SentryEnv {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_SENTRY_SEND_USER_EMAIL?: string;
  VITE_API_BASE_URL?: string;
  MODE?: string;
}

/**
 * Reads a 0..1 rate. An invalid value falls back to the default rather than to
 * `NaN`, which Sentry reads as "never sample" without saying so: a typo in the
 * variable would switch performance off and look like a broken SDK.
 */
export function readRate(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return fallback;
  return parsed;
}

/**
 * Who the trace headers are propagated to.
 *
 * In production the backend serves the SPA from the same origin, so the relative
 * URLs are enough — which is what the generated client uses. When
 * `VITE_API_BASE_URL` points at a remote backend (the development case), that
 * origin is added: without it the trace stops in the browser and the request to
 * the backend shows as a loose span, with no server transaction underneath.
 *
 * The list is deliberately short. `sentry-trace` and `baggage` on a request to a
 * third party are headers they never asked for and that break their CORS too.
 */
export function tracePropagationTargets(apiBaseUrl: string | undefined): (string | RegExp)[] {
  const targets: (string | RegExp)[] = [/^\//];
  if (apiBaseUrl && apiBaseUrl.trim() !== '') targets.push(apiBaseUrl);
  return targets;
}

/**
 * Browser noise that is not a defect of the application and that, unfiltered, is
 * most of a SPA's volume: extensions injecting scripts, the benign
 * ResizeObserver warning, and the fetches the browser itself aborts on
 * navigation or when the network drops.
 */
const IGNORED_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // The navigation cancelled the in-flight fetch. Nothing failed.
  'AbortError',
  'The operation was aborted',
  'NetworkError when attempting to fetch resource',
  'Failed to fetch',
  'Load failed',
];

/** Browser extensions: their stack is not our code. */
const DENIED_URLS = [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i];

export function buildSentryConfig(env: SentryEnv): Sentry.BrowserOptions | null {
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) return null;

  return {
    dsn,
    environment: env.VITE_SENTRY_ENVIRONMENT ?? env.MODE ?? 'development',
    // The build's commit. It is what makes an error point at a line of source
    // and not of the minified bundle (see the Vite plugin in `vite.config.ts`,
    // which uploads the source maps under this same name).
    release: env.VITE_SENTRY_RELEASE,
    integrations: [
      // Instruments pageload and navigation with react-router v7's routes. Without
      // the hooks it receives, every transaction would be named after the concrete
      // URL — `/dtes/9f0a…` — and there would be no way to group "the DTE view":
      // they would be thousands of distinct transactions, one per document.
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: readRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, DEFAULT_TRACES_SAMPLE_RATE),
    tracePropagationTargets: tracePropagationTargets(env.VITE_API_BASE_URL),
    // This app displays third parties' tax documents. No automatic PII: what
    // Sentry needs to group by is set by `setSentryUser`.
    sendDefaultPii: false,
    ignoreErrors: IGNORED_ERRORS,
    denyUrls: DENIED_URLS,
  };
}

/**
 * Starts the SDK. It returns whether it ended up active, so the caller (the
 * tests above all) can decide without reading the environment again.
 */
export function initSentry(env: SentryEnv): boolean {
  const config = buildSentryConfig(env);
  if (!config) return false;
  Sentry.init(config);
  return true;
}

export interface SentryUserContext {
  email: string;
  activeTenantId: string | null;
  activeCompanyId: string | null;
  /** `VITE_SENTRY_SEND_USER_EMAIL === 'true'`. */
  sendEmail: boolean;
}

/**
 * Identifies the session in progress.
 *
 * The **tenant and the active company** are the two values that make a report
 * actionable: here "slow" or "failed" is almost never a property of the screen
 * but of the company — one with 40,000 DTEs in the month does not run the same
 * query as one with 40 — and the active company is what decides where a folio
 * is consumed.
 *
 * The **email is not sent by default**, even though it is the only thing that
 * identifies the person. It is a customer's data in a third-party service, and
 * the backend already decided the same (`sendDefaultPii: false`). It is enabled
 * deliberately with `VITE_SENTRY_SEND_USER_EMAIL=true` when support needs it to
 * answer a ticket.
 */
export function setSentryUser({
  email,
  activeTenantId,
  activeCompanyId,
  sendEmail,
}: SentryUserContext): void {
  Sentry.setUser(sendEmail ? { email } : null);
  Sentry.setTag('tenant_id', activeTenantId ?? undefined);
  Sentry.setTag('company_id', activeCompanyId ?? undefined);
}

/** On logout: what follows belongs to neither this person nor this tenant. */
export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.setTag('tenant_id', undefined);
  Sentry.setTag('company_id', undefined);
}
